import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, CheckCircle2, Route } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtKm = (v) => (v == null ? '-' : `${Number(v).toFixed(2)} km`);
const fmtEur = (v) => (v == null ? '-' : `${Number(v).toFixed(2)} €`);
const fmtDate = (v) => (v ? new Date(v).toLocaleString('fr-FR') : '-');

const ReviewRow = ({ seg, onRecalc, busy }) => (
  <div data-testid={`segment-review-row-${seg.id}`} className="flex items-center justify-between gap-4 bg-zinc-900 border border-amber-500/30 rounded p-3">
    <div className="text-sm">
      <p className="text-white font-medium">{seg.driver_name} · semaine {seg.week_key}</p>
      <p className="text-zinc-400 text-xs">Tronçon terminé le {fmtDate(seg.ended_at)} · {seg.occupancy} abonné(s) à bord</p>
      <p className="text-amber-300 text-xs mt-1">
        Vol d'oiseau INDICATIF : {fmtKm(seg.haversine_km)} · montant non payé tant que non vérifié
      </p>
    </div>
    <Button size="sm" onClick={() => onRecalc(seg.id)} disabled={busy} data-testid={`recalc-segment-btn-${seg.id}`}>
      <RefreshCw className={`w-4 h-4 mr-1 ${busy ? 'animate-spin' : ''}`} /> Recalculer la distance
    </Button>
  </div>
);

const CorrectionRow = ({ seg }) => {
  const last = (seg.review_history || []).filter((h) => h.outcome === 'corrected').slice(-1)[0];
  if (!last) return null;
  return (
    <p data-testid={`segment-correction-${seg.id}`} className="text-xs text-emerald-300 flex items-center gap-1">
      <CheckCircle2 className="w-3 h-3" /> {seg.driver_name} ({seg.week_key}) : {fmtKm(last.old_km)} → {fmtKm(last.new_km)},{' '}
      {fmtEur(last.new_amount)} à régulariser au prochain lundi ({fmtDate(last.at)}, {last.by})
    </p>
  );
};

export const DistanceReviewPanel = () => {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/segments/review`, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error('Chargement des tronçons à vérifier impossible');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const recalc = async (id) => {
    setBusyId(id);
    try {
      const r = await axios.post(`${API}/api/admin/segments/${id}/recalculate`, {}, { headers: authHeaders() });
      toast.success(`Tronçon validé : ${fmtKm(r.data.old_km)} → ${fmtKm(r.data.new_km)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recalcul impossible');
    } finally {
      setBusyId(null);
      load();
    }
  };

  if (!data) return null;
  const usage = data.ors_usage_today || {};
  return (
    <div data-testid="distance-review-panel" className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4 mb-6 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold flex items-center gap-2"><Route className="w-4 h-4 text-[#FFD60A]" /> Tronçons à vérifier</h2>
        <span data-testid="ors-usage-today" className="text-xs text-zinc-400">
          ORS aujourd'hui : {usage.calls || 0} appels · {usage.failures || 0} échecs
        </span>
      </div>
      {data.pending.length > 0 ? (
        <p data-testid="segments-pending-banner" className="text-sm text-amber-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {data.pending.length} tronçon(s) exclu(s) du paiement jusqu'à vérification — le reste des relevés est réglé normalement le lundi.
        </p>
      ) : (
        <p data-testid="segments-review-empty" className="text-sm text-zinc-500">Aucun tronçon en attente de vérification.</p>
      )}
      {data.pending.map((s) => <ReviewRow key={s.id} seg={s} onRecalc={recalc} busy={busyId === s.id} />)}
      {data.recent_corrections.map((s) => <CorrectionRow key={s.id} seg={s} />)}
    </div>
  );
};
